import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
    FaUpload,
    FaTrash,
    FaCheckCircle,
    FaExclamationTriangle,
    FaChevronLeft,
    FaFileExcel,
    FaInfoCircle
} from "react-icons/fa";
import useAuthStore from "../../../store/useAuthStore";
import Toast from "../../../Components/Toast";

const BulkAddStaff = () => {
    const navigate = useNavigate();
    const { token } = useAuthStore();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, type: "", title: "", message: "" });

    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const validateCID = (cid) => /^\d{11}$/.test(cid);
    const validatePhone = (phone) => /^\d{7,15}$/.test(phone);

    const formatDateForInput = (dateVal) => {
        if (!dateVal) return "";
        try {
            const date = new Date(dateVal);
            if (isNaN(date.getTime())) return "";
            return date.toISOString().split("T")[0];
        } catch (e) {
            return "";
        }
    };

    const validateRow = (row) => {
        const errors = [];
        if (!row.name || String(row.name).trim().length < 2) errors.push("Name too short");
        if (!row.email || !validateEmail(String(row.email))) errors.push("Invalid Email");

        if (!row.dob) {
            errors.push("DOB required");
        } else if (isNaN(new Date(row.dob).getTime())) {
            errors.push("Invalid DOB format");
        }

        if (!row.dateOfJoining) {
            errors.push("DOJ required");
        } else if (isNaN(new Date(row.dateOfJoining).getTime())) {
            errors.push("Invalid DOJ format");
        }

        if (!row.gender || !["Male", "Female", "Other"].includes(row.gender)) errors.push("Invalid Gender");
        if (!row.role) errors.push("Role required");
        if (!row.department) errors.push("Dept required");
        if (!row.phone || !validatePhone(String(row.phone))) errors.push("Invalid Phone");
        if (!row.cid || !validateCID(String(row.cid))) errors.push("CID must be 11 digits");
        if (row.salary && isNaN(Number(row.salary))) errors.push("Invalid Salary");
        if (row.minBasicSalary && isNaN(Number(row.minBasicSalary))) errors.push("Invalid Min Salary");
        if (!row.tpn) errors.push("TPN required");
        return errors;
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: "binary", cellDates: true });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const jsonData = XLSX.utils.sheet_to_json(ws);

                const formattedData = jsonData.map((item, index) => {
                    const rowErrors = validateRow(item);
                    return {
                        ...item,
                        id: Date.now() + index,
                        status: rowErrors.length > 0 ? "error" : "pending",
                        errors: rowErrors
                    };
                });
                setData(formattedData);
            } catch (error) {
                setToast({ show: true, type: "error", title: "File Error", message: "Failed to parse file. Ensure it is a valid Excel/CSV." });
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleInputChange = (id, field, value) => {
        setData((prev) =>
            prev.map((item) => {
                if (item.id === id) {
                    const updatedItem = { ...item, [field]: value };
                    const rowErrors = validateRow(updatedItem);
                    return {
                        ...updatedItem,
                        status: rowErrors.length > 0 ? "error" : item.status === "success" ? "success" : "pending",
                        errors: rowErrors
                    };
                }
                return item;
            })
        );
    };

    const removeItem = (id) => {
        setData((prev) => prev.filter((item) => item.id !== id));
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                name: "John Doe",
                email: "john@example.com",
                dob: "1995-05-15",
                gender: "Male",
                role: "Employee",
                department: "IT",
                phone: "17123456",
                cid: "12345678901",
                salary: 25000,
                minBasicSalary: 15000,
                tpn: "TPN-123456",
                pan: "PAN-123456",
                dateOfJoining: "2024-01-01",
                employeeStatus: "Regular",
                grade: "A1",
                bankName: "BNB",
                bankAccountNumber: "0100012345678"
            },
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        XLSX.writeFile(wb, "NexrBooks_Staff_Template.xlsx");
    };

    const handleSubmit = async () => {
        const errorRows = data.filter(row => row.status === "error");
        if (errorRows.length > 0) {
            setToast({ show: true, type: "error", title: "Validation Error", message: "Please correct all highlighted errors before importing." });
            return;
        }

        if (data.length === 0) {
            setToast({ show: true, type: "error", title: "No Data", message: "Please upload a file first." });
            return;
        }

        setLoading(true);
        try {
            const response = await fetch("/api/bulkAddEmployees", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ employees: data }),
            });

            const result = await response.json();
            if (response.ok) {
                if (result.errors && result.errors.length > 0) {
                    setToast({
                        show: true,
                        type: "error",
                        title: "Partial Success",
                        message: `${result.results.length} added, ${result.errors.length} failed. Check red rows.`,
                    });
                    const updatedData = data.map(item => {
                        const serverError = result.errors.find(e => e.email === item.email);
                        return serverError ?
                            { ...item, status: "error", errors: [serverError.error] } :
                            { ...item, status: "success", errors: [] };
                    });
                    setData(updatedData);
                } else {
                    setToast({
                        show: true,
                        type: "success",
                        title: "Success",
                        message: `${result.results.length} employees added successfully!`,
                    });
                    setTimeout(() => navigate("/staffInfo"), 2000);
                }
            } else {
                setToast({ show: true, type: "error", title: "Server Error", message: result.error || "Failed to add employees." });
            }
        } catch (error) {
            setToast({ show: true, type: "error", title: "Network Error", message: "Unable to reach the server." });
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-slate-50/50 p-4 md:p-10 font-sans tracking-tight">
            <div className="max-w-[1400px] mx-auto space-y-8">

                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 px-2">
                    <div className="space-y-1">
                        <button
                            onClick={() => navigate("/staffInfo")}
                            className="flex items-center gap-2 text-slate-400 hover:text-indigo-600 font-semibold text-sm transition-all"
                        >
                            <FaChevronLeft className="text-[10px]" />
                            Staff Directory
                        </button>
                        <h1 className="text-4xl font-black text-slate-900 tracking-tighter">
                            Bulk <span className="text-indigo-600">Onboarding</span>
                        </h1>
                        <p className="text-slate-500 font-medium">Upload and verify your employee records in batch.</p>
                    </div>

                    <div className="flex flex-wrap gap-3 font-bold">
                        <button
                            onClick={downloadTemplate}
                            className="flex items-center gap-2 px-6 py-3 bg-white text-slate-700 hover:bg-slate-50 rounded-2xl border border-slate-200 shadow-sm transition-all"
                        >
                            <FaFileExcel className="text-emerald-500" />
                            Sample Template
                        </button>
                        <label className="flex items-center gap-2 px-8 py-3 bg-slate-900 text-white rounded-2xl cursor-pointer hover:bg-indigo-600 transition-all shadow-xl shadow-slate-200">
                            <FaUpload className="text-xs" />
                            Upload Spreadsheet
                            <input type="file" className="hidden" accept=".xlsx, .xls, .csv" onChange={handleFileUpload} />
                        </label>
                    </div>
                </div>

                {/* Status Dashboard */}
                {data.length > 0 && (
                    <div className="flex flex-wrap gap-4 px-2">
                        <div className="bg-white px-6 py-3 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-center">
                            <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest leading-none mb-1">Total Entries</span>
                            <span className="text-xl font-black text-slate-800">{data.length}</span>
                        </div>
                        <div className="bg-emerald-50 px-6 py-3 rounded-2xl border border-emerald-100 shadow-sm flex flex-col justify-center">
                            <span className="text-[10px] font-black uppercase text-emerald-400 tracking-widest leading-none mb-1">Valid Records</span>
                            <span className="text-xl font-black text-emerald-700">{data.filter(r => r.status === 'pending').length}</span>
                        </div>
                        <div className="bg-rose-50 px-6 py-3 rounded-2xl border border-rose-100 shadow-sm flex flex-col justify-center">
                            <span className="text-[10px] font-black uppercase text-rose-400 tracking-widest leading-none mb-1">Errors Found</span>
                            <span className="text-xl font-black text-rose-700">{data.filter(r => r.status === 'error').length}</span>
                        </div>
                    </div>
                )}

                {/* Table Container */}
                <div className="bg-white rounded-[2.5rem] border border-slate-200/60 shadow-2xl shadow-slate-200/40 overflow-hidden flex flex-col min-h-[600px]">
                    {data.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-20 text-center space-y-6">
                            <div className="w-32 h-32 bg-slate-50 border border-slate-100 rounded-3xl flex items-center justify-center text-slate-200 shadow-inner">
                                <FaFileExcel className="text-5xl" />
                            </div>
                            <div className="space-y-1">
                                <h3 className="text-2xl font-black text-slate-800 tracking-tight">Sheet Preview</h3>
                                <p className="text-slate-500 font-medium max-w-sm">
                                    Upload your onboarding file to preview and correct details before importing.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex-1 flex flex-col overflow-hidden">
                            <div className="px-10 py-8 border-b border-slate-50 flex justify-between items-center group">
                                <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">
                                    Draft Review
                                    <span className="text-[10px] bg-slate-100 px-2 py-1 rounded-full text-slate-400 uppercase tracking-widest">Editable Feed</span>
                                </h2>
                                <button onClick={() => setData([])} className="text-rose-500 font-bold hover:bg-rose-50 px-4 py-2 rounded-xl text-sm transition-all">Clear All</button>
                            </div>

                            <div className="flex-1 overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead className="sticky top-0 bg-white/90 backdrop-blur-md z-10 border-b border-slate-100">
                                        <tr className="text-slate-400 text-[10px] font-black uppercase tracking-widest">
                                            <th className="pl-10 pr-4 py-6 w-20">Status</th>
                                            <th className="px-4 py-6 min-w-[200px]">Name</th>
                                            <th className="px-4 py-6 min-w-[220px]">Email</th>
                                            <th className="px-4 py-6 min-w-[150px]">DOB</th>
                                            <th className="px-4 py-6 min-w-[150px]">DOJ</th>
                                            <th className="px-4 py-6 min-w-[120px]">Gender</th>
                                            <th className="px-4 py-6 min-w-[140px]">Role</th>
                                            <th className="px-4 py-6 min-w-[160px]">Dept.</th>
                                            <th className="px-4 py-6 min-w-[150px]">Phone</th>
                                            <th className="px-4 py-6 min-w-[150px]">CID</th>
                                            <th className="px-4 py-6 min-w-[150px]">TPN</th>
                                            <th className="px-4 py-6 min-w-[130px]">Min Basic</th>
                                            <th className="px-4 py-6 min-w-[130px]">Salary</th>
                                            <th className="px-4 py-6 min-w-[150px]">Status</th>
                                            <th className="px-4 py-6 min-w-[120px]">Grade</th>
                                            <th className="px-4 py-6 min-w-[200px]">Bank Name</th>
                                            <th className="px-4 py-6 min-w-[200px]">A/C No.</th>
                                            <th className="pr-10 pl-4 py-6 w-16"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {data.map((item) => (
                                            <tr key={item.id} className={`group ${item.status === 'error' ? 'bg-rose-50/20' : 'hover:bg-slate-50/50'} transition-colors`}>
                                                <td className="pl-10 pr-4 py-5">
                                                    <div className="flex items-center">
                                                        {item.status === "pending" && <div className="w-2.5 h-2.5 bg-slate-200 rounded-full" />}
                                                        {item.status === "success" && <FaCheckCircle className="text-emerald-500 text-lg" />}
                                                        {item.status === "error" && (
                                                            <div className="relative group/tip">
                                                                <FaExclamationTriangle className="text-rose-500 text-lg animate-pulse" />
                                                                <div className="absolute left-0 bottom-full mb-3 hidden group-hover/tip:block z-30 w-56 p-4 bg-slate-900 text-white text-[10px] font-bold rounded-2xl shadow-2xl">
                                                                    <p className="text-rose-400 mb-1 border-b border-white/10 pb-1 uppercase tracking-widest">Validation Error</p>
                                                                    {item.errors.join(", ")}
                                                                    <div className="absolute -bottom-1 left-3 w-3 h-3 bg-slate-900 rotate-45" />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-5">
                                                    <input
                                                        value={item.name || ""}
                                                        onChange={(e) => handleInputChange(item.id, "name", e.target.value)}
                                                        className="w-full bg-transparent border-none focus:ring-0 outline-none font-bold text-slate-700"
                                                    />
                                                </td>
                                                <td className="px-4 py-5">
                                                    <input
                                                        value={item.email || ""}
                                                        onChange={(e) => handleInputChange(item.id, "email", e.target.value)}
                                                        className="w-full bg-transparent border-none focus:ring-0 outline-none font-medium text-slate-500"
                                                    />
                                                </td>
                                                <td className="px-4 py-5">
                                                    <input
                                                        type="date"
                                                        value={formatDateForInput(item.dob)}
                                                        onChange={(e) => handleInputChange(item.id, "dob", e.target.value)}
                                                        className="w-full bg-transparent border-none focus:ring-0 outline-none font-medium text-slate-500"
                                                    />
                                                </td>
                                                <td className="px-4 py-5">
                                                    <input
                                                        type="date"
                                                        value={formatDateForInput(item.dateOfJoining)}
                                                        onChange={(e) => handleInputChange(item.id, "dateOfJoining", e.target.value)}
                                                        className="w-full bg-transparent border-none focus:ring-0 outline-none font-medium text-slate-500"
                                                    />
                                                </td>
                                                <td className="px-4 py-5">
                                                    <select
                                                        value={item.gender || "Male"}
                                                        onChange={(e) => handleInputChange(item.id, "gender", e.target.value)}
                                                        className="w-full bg-transparent border-none focus:ring-0 outline-none font-medium text-slate-500 cursor-pointer"
                                                    >
                                                        <option value="Male">Male</option>
                                                        <option value="Female">Female</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </td>
                                                <td className="px-4 py-5">
                                                    <select
                                                        value={item.role || "Employee"}
                                                        onChange={(e) => handleInputChange(item.id, "role", e.target.value)}
                                                        className="w-full bg-white border border-slate-100 rounded-xl py-1 px-3 text-[10px] font-black text-indigo-600 uppercase shadow-sm outline-none"
                                                    >
                                                        <option value="Employee">Employee</option>
                                                        <option value="HR">HR</option>
                                                        <option value="Accounts">Accounts</option>
                                                    </select>
                                                </td>
                                                <td className="px-4 py-5">
                                                    <input
                                                        value={item.department || ""}
                                                        onChange={(e) => handleInputChange(item.id, "department", e.target.value)}
                                                        className="w-full bg-transparent border-none focus:ring-0 outline-none font-semibold text-slate-600"
                                                    />
                                                </td>
                                                <td className="px-4 py-5">
                                                    <input
                                                        value={item.phone || ""}
                                                        onChange={(e) => handleInputChange(item.id, "phone", e.target.value)}
                                                        className="w-full bg-transparent border-none focus:ring-0 outline-none font-medium text-slate-500"
                                                    />
                                                </td>
                                                <td className="px-4 py-5">
                                                    <input
                                                        value={item.cid || ""}
                                                        onChange={(e) => handleInputChange(item.id, "cid", e.target.value)}
                                                        className="w-full bg-transparent border-none focus:ring-0 outline-none font-medium text-slate-500"
                                                    />
                                                </td>
                                                <td className="px-4 py-5">
                                                    <input
                                                        value={item.tpn || ""}
                                                        onChange={(e) => handleInputChange(item.id, "tpn", e.target.value)}
                                                        className="w-full bg-transparent border-none focus:ring-0 outline-none font-medium text-slate-500"
                                                    />
                                                </td>
                                                <td className="px-4 py-5">
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-[10px] font-black text-slate-300">Nu.</span>
                                                        <input
                                                            type="number"
                                                            value={item.minBasicSalary || ""}
                                                            onChange={(e) => handleInputChange(item.id, "minBasicSalary", e.target.value)}
                                                            className="w-full bg-transparent border-none focus:ring-0 outline-none font-black text-slate-800"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-4 py-5">
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-[10px] font-black text-slate-300">Nu.</span>
                                                        <input
                                                            type="number"
                                                            value={item.salary || ""}
                                                            onChange={(e) => handleInputChange(item.id, "salary", e.target.value)}
                                                            className="w-full bg-transparent border-none focus:ring-0 outline-none font-black text-slate-800"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-4 py-5">
                                                    <select
                                                        value={item.employeeStatus || ""}
                                                        onChange={(e) => handleInputChange(item.id, "employeeStatus", e.target.value)}
                                                        className="w-full bg-transparent border-none focus:ring-0 outline-none font-medium text-slate-500 cursor-pointer"
                                                    >
                                                        <option value="">Select Status</option>
                                                        <option value="Contract">Contract</option>
                                                        <option value="Regular">Regular</option>
                                                        <option value="Wages">Wages</option>
                                                        <option value="Probation">Probation</option>
                                                    </select>
                                                </td>
                                                <td className="px-4 py-5">
                                                    <input
                                                        value={item.grade || ""}
                                                        onChange={(e) => handleInputChange(item.id, "grade", e.target.value)}
                                                        className="w-full bg-transparent border-none focus:ring-0 outline-none font-medium text-slate-500"
                                                    />
                                                </td>
                                                <td className="px-4 py-5">
                                                    <input
                                                        value={item.bankName || ""}
                                                        onChange={(e) => handleInputChange(item.id, "bankName", e.target.value)}
                                                        className="w-full bg-transparent border-none focus:ring-0 outline-none font-medium text-slate-500"
                                                    />
                                                </td>
                                                <td className="px-4 py-5">
                                                    <input
                                                        value={item.bankAccountNumber || ""}
                                                        onChange={(e) => handleInputChange(item.id, "bankAccountNumber", e.target.value)}
                                                        className="w-full bg-transparent border-none focus:ring-0 outline-none font-medium text-slate-500"
                                                    />
                                                </td>
                                                <td className="pr-10 pl-4 py-5 text-right">
                                                    <button onClick={() => removeItem(item.id)} className="opacity-0 group-hover:opacity-100 p-2 text-slate-300 hover:text-rose-500 transition-all">
                                                        <FaTrash />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Footer Commit Section */}
                            <div className="p-10 bg-slate-50/50 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-10">
                                <div className="flex items-center gap-5 text-slate-400">
                                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-indigo-500 shadow-sm">
                                        <FaInfoCircle className="text-xl" />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-black text-slate-700 leading-none">Security Check</h4>
                                        <p className="text-[10px] font-bold uppercase mt-1 tracking-widest text-slate-400">Records are validated locally before sync.</p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleSubmit}
                                    disabled={loading || data.some(r => r.status === 'error')}
                                    className="w-full md:w-auto px-16 py-5 bg-slate-900 text-white rounded-[1.8rem] font-black hover:bg-indigo-600 transition-all shadow-2xl shadow-slate-200 disabled:bg-slate-200 disabled:shadow-none disabled:text-slate-400 flex items-center justify-center gap-4"
                                >
                                    {loading ? (
                                        <>
                                            <div className="w-5 h-5 border-4 border-white/20 border-t-white rounded-full animate-spin" />
                                            Syncing Records...
                                        </>
                                    ) : (
                                        <>Finalize Import</>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {toast.show && (
                <Toast
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                    onClose={() => setToast({ ...toast, show: false })}
                />
            )}
        </div>
    );
};

export default BulkAddStaff;
